import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/base/StructuredData';
import { articles } from '@/mocks/community';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-error-warning-line text-3xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-4">您访问的文章不存在或已被移除</p>
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    headline: article.title,
    description: article.summary,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    image: article.coverImage,
    url: `${window.location.origin}/article/${article.slug}`,
  };

  return (
    <div className="min-h-screen pt-20">
      <StructuredData type="Article" data={articleSchema} />

      <article className="bg-background-50">
        {/* Article Header */}
        <div className="bg-background-100 py-8 md:py-10">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: article.categoryName, to: `/category/${article.categoryId}` },
                { label: article.title },
              ]}
            />

            <div className="mt-5">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.categoryId}`}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-primary-200 transition-colors duration-200"
                >
                  {article.categoryName}
                </Link>
              </div>
              <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 mt-3 leading-relaxed">{article.summary}</p>
              <div className="flex items-center gap-4 mt-5 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <img src={article.authorAvatar} alt={article.author} className="w-7 h-7 rounded-full object-cover" />
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span>·</span>
                <time dateTime={article.publishedAt}>{article.publishedAt}</time>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount >= 1000 ? `${(article.readCount / 1000).toFixed(1)}k 阅读` : `${article.readCount} 阅读`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Cover Image */}
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto mt-8">
          <img
            src={article.coverImage}
            alt={article.title}
            title={article.title}
            className="w-full rounded-xl object-cover object-top h-56 md:h-80"
          />
        </div>

        {/* Article Content */}
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto py-8 md:py-10">
          <div
            className="article-content prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="flex items-center flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Interaction Bar */}
          <div className="flex items-center gap-6 mt-8 py-6 border-t border-background-200/70">
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium cursor-pointer hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
              <i className="ri-heart-line"></i>
              点赞 ({article.likeCount})
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium cursor-pointer hover:bg-secondary-50 hover:text-secondary-600 transition-colors duration-200 whitespace-nowrap">
              <i className="ri-chat-1-line"></i>
              评论 ({article.commentCount})
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium cursor-pointer hover:bg-accent-50 hover:text-accent-600 transition-colors duration-200 whitespace-nowrap">
              <i className="ri-bookmark-line"></i>
              收藏
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium cursor-pointer hover:bg-background-200 transition-colors duration-200 whitespace-nowrap">
              <i className="ri-share-forward-line"></i>
              分享
            </button>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-6">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group cursor-pointer bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-200"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      title={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{ra.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}